import capitalize from 'lodash/capitalize';
import classNames from 'classnames';
import CloseIcon from '@mui/icons-material/Close';

import ELEMENT, { createTestId } from 'common/element';

import Button, { ButtonVariant } from 'components/common/Button/Button';

import { HelpItem } from './help';

import './SearchHelpPanel.scss';

/**
 * The help panel appears to the right of the list of matches and provides additional information about the
 * currently selected match.
 *
 * @prop item    object containing help content
 */

type SearchHelpPanelProps = {
  item:     HelpItem;
  helpLink: string;
  close:    () => void;
};

/**
 * This component displays help for a function or a Power Query command. A description, syntax, and examples are shown.
 *
 * @prop item        object with help information
 * @prop helpLink    URL for more complete documentation
 * @prop close       function that hides the panel
 */
function SearchHelpPanel({
  item,
  helpLink,
  close,
}: SearchHelpPanelProps): JSX.Element {

  // format a value for 'syntax' or 'example', which may be a list; indent if needed
  const getBlock = (field: string, value?: string | string[]): JSX.Element | null => {

    if (!value) {
      return <></>;
    }

    const values = Array.isArray(value) ? value : [ value ];
    const content = values.map((val, index) => {
      const key = [ field, index ].join('-');
      const className = classNames('SearchHelpPanel__Content', {
        'SearchHelpPanel__Content--Indent': val.startsWith(' '),
      });
      return <div className={className} key={key}>{val}</div>;
    });

    return (
      <div className="SearchHelpPanel__Block">
        <div className="SearchHelpPanel__Topic">{capitalize(field)}</div>
        {content}
      </div>
    );
  };

  const helpButton = (
    <Button
      variant={ButtonVariant.BORDERLESS}
      href={helpLink}
      openInNewTab
      testId={createTestId(ELEMENT.powerQueryCommandHelp, item.name)}
    >
      More Help
    </Button>
  );

  const closeButton = (
    <span
      className="SearchHelpPanel__Close"
      onClick={close}
      role="button"
      tabIndex={0}
      aria-label="Close"
      data-test-id={ELEMENT.searchHelpClose}
    >
      <CloseIcon fontSize="small" />
    </span>
  );

  // some commands have a sample results table
  let sampleResults = null;
  if (item.header && item.rows) {
    const headerRow = item.header.map((col) => <th className="SearchHelpPanel__SampleResultsHeader" key={createTestId(item.name, col)}>{col}</th>);
    const resultRows = item.rows.map((row, rowIdx) => (
      <tr className="SearchHelpPanel__SampleResultsRow" key={createTestId(item.name, rowIdx)}>
        {row.map((val, colIdx) => <td className="SearchHelpPanel__SampleResultsCell" key={createTestId(item.name, rowIdx, colIdx)}>{val}</td>)}
      </tr>
    ));
    sampleResults = (
      <table className="SearchHelpPanel__SampleResultsTable">
        <thead>
          <tr>{headerRow}</tr>
        </thead>
        <tbody>
          {resultRows}
        </tbody>
      </table>
    );
  }

  return (
    <div className="SearchHelpPanel" data-test-id={createTestId(ELEMENT.searchHelpCommand, item.name)}>
      <div className="SearchHelpPanel__Header">
        <span className="SearchHelpPanel__Name">
          {item.name}
        </span>
        <div>
          {helpButton}
          {closeButton}
        </div>
      </div>
      <div className="SearchHelpPanel__Divider" />
      <div className="SearchHelpPanel__Description">
        {getBlock('syntax', item.syntax)}
        <div className="SearchHelpPanel__Block">
          <div className="SearchHelpPanel__Text">{item.description}</div>
        </div>
        {getBlock('example', item.example)}
        {sampleResults}
      </div>
    </div>
  );
}

export default SearchHelpPanel;
